import { useEffect } from "react";
import { SITE_NAME, SITE_URL, DEFAULT_DESCRIPTION } from "../../config";

const upsertMeta = (selector, identifierAttrs, content) => {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    Object.entries(identifierAttrs).forEach(([key, value]) =>
      el.setAttribute(key, value)
    );
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("content");
  el.setAttribute("content", content || "");
  return () => {
    if (created) {
      el.remove();
    } else if (prev !== null) {
      el.setAttribute("content", prev);
    }
  };
};

const upsertCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return () => {
    if (created) {
      el.remove();
    } else if (prev !== null) {
      el.setAttribute("href", prev);
    }
  };
};

const injectJsonLd = (data) => {
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.setAttribute("data-seo", "jsonld");
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
  return () => script.remove();
};

const Seo = ({ title, description, image, type = "website", canonical, jsonLd }) => {
  useEffect(() => {
    const fullTitle = title
      ? `${title} | ${SITE_NAME}`
      : `${SITE_NAME} - Afazi Teknolojileri`;
    const desc = description || DEFAULT_DESCRIPTION;
    const canonicalUrl = canonical
      ? new URL(canonical, SITE_URL).href
      : SITE_URL + window.location.pathname;
    const absoluteImage = image
      ? new URL(image, SITE_URL).href
      : undefined;

    const prevTitle = document.title;
    document.title = fullTitle;

    const cleanups = [
      upsertCanonical(canonicalUrl),
      upsertMeta('meta[name="description"]', { name: "description" }, desc),
      upsertMeta('meta[property="og:title"]', { property: "og:title" }, fullTitle),
      upsertMeta('meta[property="og:description"]', { property: "og:description" }, desc),
      upsertMeta('meta[property="og:type"]', { property: "og:type" }, type),
      upsertMeta('meta[property="og:url"]', { property: "og:url" }, canonicalUrl),
      upsertMeta('meta[property="og:site_name"]', { property: "og:site_name" }, SITE_NAME),
      upsertMeta('meta[name="twitter:card"]', { name: "twitter:card" }, "summary_large_image"),
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title" }, fullTitle),
      upsertMeta('meta[name="twitter:description"]', { name: "twitter:description" }, desc),
    ];

    if (absoluteImage) {
      cleanups.push(
        upsertMeta('meta[property="og:image"]', { property: "og:image" }, absoluteImage)
      );
      cleanups.push(
        upsertMeta('meta[name="twitter:image"]', { name: "twitter:image" }, absoluteImage)
      );
    }

    if (jsonLd) {
      cleanups.push(injectJsonLd(jsonLd));
    }

    return () => {
      document.title = prevTitle;
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [title, description, image, type, canonical, jsonLd]);

  return null;
};

export default Seo;
